/* ─── 多 pass 定义(单一数据源) ─── */

/** 全部 pass 标签; 数组顺序即渲染顺序: buffer → cubemap → image */
export const PASS_KINDS = [
  'bufferA', 'bufferB', 'bufferC', 'bufferD', 'cubemapA', 'image',
] as const
export type PassKind = (typeof PASS_KINDS)[number]

/** 非 pass 的围栏标签(解析期使用) */
export const BLOCK_LANG = { common: 'common', sound: 'sound', glsl: 'glsl' } as const

/** 离屏双缓冲 pass(支持自反馈), 渲染顺序在 cubemap/image 之前 */
export const BUFFER_KINDS: readonly PassKind[] = ['bufferA', 'bufferB', 'bufferC', 'bufferD']

/** 通道目标特殊值: 程序化噪点纹理('cube' 是 noiseCube 的用户注释别名) */
export const NOISE2D = 'noise2d'
export const NOISE_CUBE = 'noiseCube'
export const CUBE_ALIAS = 'cube'
/** 白噪点变体(hash 查表风格 value noise 的格点随机源) */
export const HASHNOISE = 'hashnoise'

/** 常用单例, 避免散落字符串字面量 */
export const IMAGE: PassKind = 'image'
export const CUBEMAP: PassKind = 'cubemapA'

/** 类型守卫: 字符串是否为合法 pass 标签 */
export function isPassKind(s: string): s is PassKind {
  return (PASS_KINDS as readonly string[]).includes(s)
}

export const FPS_CAP = 60 // 壁纸帧率上限: 高刷屏上避免满速空转, 节省 GPU 功耗; 可调
export const CUBE_FACES = [
  0x8515, // TEXTURE_CUBE_MAP_POSITIVE_X
  0x8516, // TEXTURE_CUBE_MAP_NEGATIVE_X
  0x8517, // TEXTURE_CUBE_MAP_POSITIVE_Y
  0x8518, // TEXTURE_CUBE_MAP_NEGATIVE_Y
  0x8519, // TEXTURE_CUBE_MAP_POSITIVE_Z
  0x851A, // TEXTURE_CUBE_MAP_NEGATIVE_Z
] as const

/** 离屏 buffer 最大边长: 全屏壁纸(高 DPR)下 RGBA32F 双缓冲显存易爆, 保持宽高比钳制 */
export const MAX_BUFFER_SIZE = 2160

/** 采样器 filter 名称(none/linear/mipmap) */
export const FILTERS = ['none', 'linear', 'mipmap'] as const
export type FilterName = (typeof FILTERS)[number]
/** 采样器 wrap 名称(clamp/repeat) */
export const WRAPS = ['clamp', 'repeat'] as const
export type WrapName = (typeof WRAPS)[number]

export function isFilter(s: string): s is FilterName { return (FILTERS as readonly string[]).includes(s) }
export function isWrap(s: string): s is WrapName { return (WRAPS as readonly string[]).includes(s) }

/**
 * 通道目标: pass 名(动态匹配已编译 pass 的 kind) / 噪点特殊值。
 * filter / wrap 可选: 未指定用纹理默认采样参数(自动); 显式指定则覆盖。
 */
export interface ChannelSpec {
  target: string
  filter?: FilterName
  wrap?: WrapName
}

export interface PassSpec {
  kind: PassKind
  code: string
  channels: ChannelSpec[]
  w: number
  h: number
}

export interface ParsedSource {
  common: string
  passes: PassSpec[]
}

/** Shadertoy 标准 uniform 的 location(强类型, 拼错编译期报错) */
export interface UniformLocs {
  iTime: WebGLUniformLocation | null
  iResolution: WebGLUniformLocation | null
  iMouse: WebGLUniformLocation | null
  iDate: WebGLUniformLocation | null
  iFrame: WebGLUniformLocation | null
  iTimeDelta: WebGLUniformLocation | null
  iFrameRate: WebGLUniformLocation | null
  iSampleRate: WebGLUniformLocation | null
}

export interface PassRuntime {
  spec: PassSpec
  prog: WebGLProgram
  uLoc: UniformLocs
  cTimeLoc: WebGLUniformLocation | null
  resLoc: WebGLUniformLocation | null
  faceLoc: WebGLUniformLocation | null
  fbo: WebGLFramebuffer | null
  textures: WebGLTexture[]
  /** 各通道的程序化噪点纹理实例(懒创建, 每通道独立, 采样参数互不覆盖) */
  noiseTex: (WebGLTexture | null)[]
  readIdx: number
  /** 本 pass 的 iFrame(每 pass 独立递增) */
  frame: number
  /** 本 pass 输出被下游以 mipmap 采样 → 渲染后需生成 mipmap 链 */
  mipmapNeeded: boolean
  w: number
  h: number
  chanRes: Float32Array
}